// src/pages/Properties.jsx
import React from 'react'
import { useNavigate } from 'react-router-dom'
import Hero from '../components/Hero'
import Card3D from '../components/Card3D'
import Carousel from '../components/Carousel'

const Properties = () => {
  const navigate = useNavigate()

  const propertyServices = [
    {
      title: 'Property Valuation',
      description: 'Accurate market value assessments for residential, commercial, and industrial properties.',
      icon: '💰',
      features: [
        'RICS compliant valuations',
        'Market value assessment',
        'Investment value analysis',
        'Comprehensive reporting'
      ],
      deliverables: [
        'Detailed valuation report',
        'Market analysis',
        'Comparative analysis',
        'Supporting documentation'
      ]
    },
    {
      title: 'Portfolio Management',
      description: 'Professional management of property portfolios for optimal performance and returns.',
      icon: '📈',
      features: [
        'Portfolio optimization',
        'Performance monitoring',
        'Risk management',
        'Strategic planning'
      ],
      deliverables: [
        'Portfolio performance reports',
        'Strategic recommendations',
        'Risk assessment reports',
        'Implementation plans'
      ]
    },
    {
      title: 'Property Inspection',
      description: 'Comprehensive property inspections and condition assessments.',
      icon: '🔍',
      features: [
        'Structural assessment',
        'Condition reporting',
        'Defect identification',
        'Maintenance planning'
      ],
      deliverables: [
        'Inspection reports',
        'Condition assessments',
        'Maintenance schedules',
        'Cost estimates'
      ]
    },
    {
      title: 'Due Diligence',
      description: 'Thorough property due diligence for acquisitions and investments.',
      icon: '📋',
      features: [
        'Legal compliance review',
        'Title verification',
        'Environmental assessment',
        'Risk analysis'
      ],
      deliverables: [
        'Due diligence reports',
        'Risk assessment',
        'Compliance certificates',
        'Recommendation memos'
      ]
    },
    {
      title: 'Rental Valuation',
      description: 'Market-based rental assessments for leasing and tenancy agreements.',
      icon: '🏘️',
      features: [
        'Market rental analysis',
        'Lease structuring',
        'Rental optimization',
        'Tenant screening support'
      ],
      deliverables: [
        'Rental valuation reports',
        'Market comparables',
        'Lease recommendations',
        'Rental strategy'
      ]
    },
    {
      title: 'Development Appraisal',
      description: 'Financial feasibility analysis for property development projects.',
      icon: '🏗️',
      features: [
        'Feasibility studies',
        'Cost-benefit analysis',
        'Development viability',
        'Return on investment'
      ],
      deliverables: [
        'Development appraisals',
        'Financial models',
        'Risk assessments',
        'Investment recommendations'
      ]
    }
  ]

  const valuationProcess = [
    {
      step: 1,
      title: 'Instruction & Briefing',
      description: 'Understanding client requirements and property specifics',
      tasks: ['Client consultation', 'Scope definition', 'Document review']
    },
    {
      step: 2,
      title: 'Site Inspection',
      description: 'Physical inspection and data collection at property location',
      tasks: ['Property measurement', 'Condition assessment', 'Photo documentation']
    },
    {
      step: 3,
      title: 'Market Research',
      description: 'Comprehensive analysis of comparable properties and market trends',
      tasks: ['Data collection', 'Market analysis', 'Comparable research']
    },
    {
      step: 4,
      title: 'Valuation Analysis',
      description: 'Application of appropriate valuation methods and calculations',
      tasks: ['Method selection', 'Data analysis', 'Value calculation']
    },
    {
      step: 5,
      title: 'Report Preparation',
      description: 'Compilation of comprehensive valuation report with findings',
      tasks: ['Report drafting', 'Quality review', 'Finalization']
    },
    {
      step: 6,
      title: 'Client Delivery',
      description: 'Presentation of final valuation report and findings to client',
      tasks: ['Report delivery', 'Findings presentation', 'Client consultation']
    }
  ]

  const propertyTypes = [
    {
      type: 'Residential',
      examples: ['Apartments', 'Houses', 'Townhouses', 'Villas'],
      valuationMethods: ['Comparable Sales', 'Income Approach', 'Cost Approach'],
      typicalTimeline: '3-5 business days'
    },
    {
      type: 'Commercial',
      examples: ['Office Buildings', 'Retail Spaces', 'Shopping Malls', 'Hotels'],
      valuationMethods: ['Income Capitalization', 'Comparable Sales', 'DCF Analysis'],
      typicalTimeline: '1-2 weeks'
    },
    {
      type: 'Industrial',
      examples: ['Warehouses', 'Factories', 'Logistics Centers', 'Manufacturing'],
      valuationMethods: ['Cost Approach', 'Income Approach', 'Sales Comparison'],
      typicalTimeline: '1-2 weeks'
    },
    {
      type: 'Land & Development',
      examples: ['Vacant Land', 'Development Sites', 'Agricultural Land'],
      valuationMethods: ['Residual Method', 'Comparable Sales', 'Development Appraisal'],
      typicalTimeline: '2-3 weeks'
    }
  ]

  const caseStudies = [
    {
      property: 'Commercial Office Tower - Accra',
      client: 'Prime Office Developments',
      challenge: 'Valuation for refinancing of 15-story office building',
      solution: 'Comprehensive income approach with market comparables',
      result: 'Successful refinancing at optimal valuation',
      value: '$25M'
    },
    {
      property: 'Mixed-Use Development - Lagos',
      client: 'Urban Space Developers',
      challenge: 'Development appraisal for 5-acre mixed-use project',
      solution: 'Residual valuation with phased development analysis',
      result: 'Secured development funding with 22% ROI projection',
      value: '$45M'
    },
    {
      property: 'Industrial Warehouse Portfolio',
      client: 'Logistics Africa Ltd',
      challenge: 'Portfolio valuation for acquisition due diligence',
      solution: 'Standardized valuation across 8 properties in 3 countries',
      result: 'Informed acquisition decision with 15% cost savings',
      value: '$18M'
    }
  ]

  return (
    <div>
      <Hero 
        title="OFANDA PROPERTIES"
        subtitle="Comprehensive property valuation and management services across Africa"
      />

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-6">
                Professional Property Services
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Our property services division specializes in comprehensive valuation, management, 
                and advisory services for all property types across African markets. We combine 
                international standards with local market expertise to deliver accurate and reliable results.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                From single residential properties to large commercial portfolios, our team provides 
                the insights you need for informed decision-making.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/contact')}
                  className="bg-ofanda-yellow text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-yellow-600 transition-colors duration-300"
                >
                  Request Valuation
                </button>
                <button className="border-2 border-ofanda-yellow text-ofanda-yellow px-8 py-3 rounded-lg font-semibold hover:bg-ofanda-yellow hover:text-gray-900 transition-colors duration-300">
                  Download Valuation Guide
                </button>
              </div>
            </div>
            <div className="bg-gray-100 rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Why Choose Us?</h3>
              <div className="space-y-4">
                {[
                  'RICS certified valuers',
                  'Local market expertise',
                  'International standards',
                  'Quick turnaround times',
                  'Competitive pricing',
                  'Comprehensive reporting'
                ].map((point, index) => (
                  <div key={index} className="flex items-center">
                    <svg className="w-5 h-5 text-ofanda-yellow mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
              Our Property Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive solutions for all your property valuation and management needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {propertyServices.map((service, index) => (
              <Card3D key={index}>
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Key Features:</h4>
                  <ul className="space-y-1">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                        <svg className="w-3 h-3 text-ofanda-yellow mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2 text-sm">Deliverables:</h4>
                  <ul className="space-y-1">
                    {service.deliverables.map((deliverable, deliverableIndex) => (
                      <li key={deliverableIndex} className="flex items-center text-sm text-gray-700">
                        <svg className="w-3 h-3 text-ofanda-yellow mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {deliverable}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card3D>
            ))}
          </div>
        </div>
      </section>

      {/* Property Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
              Property Types We Value
            </h2>
            <p className="text-xl text-gray-600">
              Expertise across all property categories and asset classes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {propertyTypes.map((propertyType, index) => (
              <Card3D key={index}>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{propertyType.type}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Examples:</h4>
                    <ul className="space-y-1">
                      {propertyType.examples.map((example, exampleIndex) => (
                        <li key={exampleIndex} className="flex items-center text-sm text-gray-700">
                          <svg className="w-3 h-3 text-ofanda-yellow mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm">Valuation Methods:</h4>
                    <ul className="space-y-1">
                      {propertyType.valuationMethods.map((method, methodIndex) => (
                        <li key={methodIndex} className="flex items-center text-sm text-gray-700">
                          <svg className="w-3 h-3 text-ofanda-yellow mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {method}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-semibold text-gray-900">Typical Timeline:</span>
                    <span className="text-ofanda-yellow font-semibold">{propertyType.typicalTimeline}</span>
                  </div>
                </div>
              </Card3D>
            ))}
          </div>
        </div>
      </section>

      {/* Valuation Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
              Our Valuation Process
            </h2>
            <p className="text-xl text-gray-600">
              Structured approach ensuring accuracy, reliability, and compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {valuationProcess.map((step, index) => (
              <Card3D key={index}>
                <div className="bg-ofanda-yellow text-white rounded-full w-12 h-12 flex items-center justify-center text-lg font-bold mb-4">
                  {step.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600 mb-4">{step.description}</p>
                <ul className="space-y-2">
                  {step.tasks.map((task, taskIndex) => (
                    <li key={taskIndex} className="flex items-center text-sm text-gray-700">
                      <svg className="w-3 h-3 text-ofanda-yellow mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {task}
                    </li>
                  ))}
                </ul>
              </Card3D>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
              Property Valuation Case Studies
            </h2>
            <p className="text-xl text-gray-600">
              Real-world examples of our property valuation expertise
            </p>
          </div>

          <Carousel
            items={caseStudies.map((study, index) => (
              <Card3D key={index} className="h-full">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">{study.property}</h3>
                  <span className="bg-ofanda-yellow text-gray-900 px-3 py-1 rounded-full text-sm font-semibold">
                    {study.value}
                  </span>
                </div>
                
                <div className="space-y-3 mb-4">
                  <div>
                    <span className="font-semibold text-gray-700">Client:</span>
                    <span className="text-gray-600 ml-2">{study.client}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Challenge:</span>
                    <span className="text-gray-600 ml-2">{study.challenge}</span>
                  </div>
                  <div>
                    <span className="font-semibold text-gray-700">Solution:</span>
                    <span className="text-gray-600 ml-2">{study.solution}</span>
                  </div>
                </div>
                
                <div className="bg-green-50 border-l-4 border-green-500 pl-4 py-2">
                  <span className="font-semibold text-gray-900">Result:</span>
                  <span className="text-gray-700 ml-2">{study.result}</span>
                </div>
              </Card3D>
            ))}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-ofanda-yellow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
            Need a Professional Property Valuation?
          </h2>
          <p className="text-xl text-gray-800 mb-8">
            Contact us today for accurate, reliable property valuations you can trust.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="bg-gray-900 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors duration-300"
            >
              Request Valuation
            </button>
            <button className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300">
              Download Service Brochure
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Properties